import { Col, Row } from 'antd';
import type {
  ShiftBackgroundColours,
  ShiftPeriod,
  Shift,
  IdentifiedShiftPeriod,
} from './types';
import { splicedShift } from './lib/util';
import { startOfDay } from 'date-fns';
import { differenceInMillisecondsWithoutTimezone } from './lib/shift-service';

type ShiftBackgroundProps = {
  /**
   * The designated 'day shift' time period
   */
  readonly dayShiftPeriod: ShiftPeriod;
  /**
   * The shift to be represented
   */
  readonly shift: Shift;
  /**
   * The colours to represent day and night
   */
  readonly shiftBackgroundColours: ShiftBackgroundColours;
  /**
   * If the shift is vacant, will render different colour.
   */
  readonly isVacant: boolean;
  readonly hasViolation?: boolean;
};

/**
 * Returns the equivalent number of milliseconds of the start and end of the shift,
 * since midnight on the day the shift started.
 */
export const shiftPeriodSinceMidnight = (shift: Shift): ShiftPeriod => {
  const startTime = new Date(shift.startTime);

  const endTime = new Date(shift.endTime);

  const midNightThisDay = startOfDay(startTime);

  const periodToStart = differenceInMillisecondsWithoutTimezone(
    startTime,
    midNightThisDay
  );

  const periodToEnd = differenceInMillisecondsWithoutTimezone(
    endTime,
    midNightThisDay
  );

  return {
    start: periodToStart,
    end: periodToEnd,
  };
};

/**
 * Returns a backdrop for the shift button, where the shift is represented by adjacent columns of the provided colours.
 * @param props The shift background props.
 * @returns a Row split into columns of two colours, each representing a day or night portion of the given shift
 */
const ShiftBackground = ({
  dayShiftPeriod,
  shift,
  shiftBackgroundColours,
  isVacant,
  hasViolation,
}: ShiftBackgroundProps) => {
  const shiftPeriodsLengths = shiftPeriodSinceMidnight(shift);

  const endTime = new Date(shift.endTime);

  const startTime = new Date(shift.startTime);

  const separatedShiftPeriods = splicedShift(
    dayShiftPeriod,
    shiftPeriodsLengths
  );

  const shiftLength = differenceInMillisecondsWithoutTimezone(
    endTime,
    startTime
  );

  const columnSpan = (period: IdentifiedShiftPeriod) =>
    // Denominator is checked here. Can't be 0.
    // eslint-disable-next-line total-functions/no-partial-division
    shiftLength > 0 ? Math.round((period.length / shiftLength) * 24) : 0;

  return (
    <Row
      className="shift-background"
      style={{
        position: 'absolute',
        height: '100%',
        width: '100%',
        border: '1px solid white',
        overflow: 'hidden',
      }}
      wrap={false}
    >
      {separatedShiftPeriods.map((period, i) => {
        const backgroundColor = () => {
          if (hasViolation === true) {
            return shiftBackgroundColours.violation;
          }
          if (isVacant && period.isDayShift) {
            return shiftBackgroundColours.vacantDay;
          }

          if (isVacant) {
            return shiftBackgroundColours.vacantNight;
          }

          if (period.isDayShift) {
            return shiftBackgroundColours.day;
          }

          return shiftBackgroundColours.night;
        };

        return (
          <Col
            key={i}
            span={columnSpan(period)}
            style={{
              height: '100%',
            }}
          >
            <div
              style={{
                backgroundColor: backgroundColor(),
                width: '100%',
                height: '100%',
              }}
            ></div>
          </Col>
        );
      })}
    </Row>
  );
};

export default ShiftBackground;
